import { cartAtom, newCartItemAtom, userAtom } from './atoms';
import { atom } from 'jotai';
import {
  AddToCartOptions,
  Cart,
  DeliveryType,
  OrderDelivery,
  OrderDeliveryAddress,
  calculateLineValues,
  changeDeliveryAddress,
  changeDeliveryDates,
  changeLocationID,
  changeSiloId,
  createNewDeliveryItem,
  createNewOrderDelivery,
  findExistingIndices,
  moveBetweenDeliveries,
  removeItemFromCart,
  removeItemFromDelivery,
  setCartItemQuantity,
  updateExistingItem,
} from '@agrantis/cart';

export const addToCartAtom = atom(
  () => '',
  (get, set, options: AddToCartOptions) => {
    const user = get(userAtom);
    const newCartItem = get(newCartItemAtom);
    if (!newCartItem || !options.retailerID) return;
    const cart = get(cartAtom);
    let retailerCart: OrderDelivery[] = cart[options.retailerID] || [];

    const deliveryType =
      options.deliveryMethod ||
      newCartItem.quantityOptions?.default?.toUpperCase() ||
      DeliveryType.Delivery;

    const { existingDeliveryIndex, existingItemIndex } = findExistingIndices(
      retailerCart,
      newCartItem,
      deliveryType,
    );

    const { lineTotalNet, lineTax, price, quantity, article } =
      calculateLineValues(newCartItem, options);

    if (existingDeliveryIndex !== -1 && existingItemIndex !== -1) {
      retailerCart = updateExistingItem(
        retailerCart,
        existingDeliveryIndex,
        existingItemIndex,
        quantity,
        lineTotalNet,
        lineTax,
      );
    } else {
      const deliveryItem = createNewDeliveryItem(
        quantity,
        price || 0,
        lineTotalNet,
        lineTax,
        article,
      );

      if (existingDeliveryIndex !== -1) {
        retailerCart[existingDeliveryIndex].items.push(deliveryItem);
      } else {
        const orderDelivery = createNewOrderDelivery(
          user,
          deliveryItem,
          deliveryType,
        );

        retailerCart.push(orderDelivery);
      }
    }

    set(cartAtom, {
      ...cart,
      [options.retailerID]: retailerCart,
    });

    set(newCartItemAtom, null);
  },
);

export const removeFromCartAtom = atom(
  () => '',
  (
    get,
    set,
    options: {
      retailerID: string;
      itemId: string;
      quantity: number;
      itemType: string;
    },
  ) => {
    const cart = get(cartAtom);
    const { selectedCart: updatedRetailerCart, itemRemoved } =
      removeItemFromCart(
        cart,
        options.retailerID,
        options.itemId,
        options.quantity,
        options.itemType,
      );

    if (itemRemoved) {
      set(cartAtom, {
        ...cart,
        [options.retailerID]: updatedRetailerCart,
      });
    }
  },
);

type RemoveFromDeliveryOptions = {
  retailerId: string;
  deliveryId: string;
  catalogueIds: string[];
};

export const removeFromDeliveryAtom = atom(
  null,
  (
    get,
    set,
    { retailerId, deliveryId, catalogueIds }: RemoveFromDeliveryOptions,
  ) => {
    const cart = get(cartAtom);

    const { cart: updatedRetailerCart, deliveryRemoved } =
      removeItemFromDelivery(cart, retailerId, deliveryId, catalogueIds);

    // Update the cart.
    if (deliveryRemoved) {
      set(cartAtom, { ...updatedRetailerCart });
    }
  },
);

type MoveBetweenDeliveriesOptions = {
  retailerId: string;
  sourceDeliveryId: string;
  targetDeliveryId: string | null;
  catalogueIds: string[];
  siloId?: string;
};

export const moveBetweenDeliveriesAtom = atom(
  null,
  (
    get,
    set,
    {
      retailerId,
      sourceDeliveryId,
      targetDeliveryId,
      catalogueIds,
      siloId,
    }: MoveBetweenDeliveriesOptions,
  ) => {
    // NOTE: id === '0' means that the destination is the pickup, not the delivery.
    const cart: Cart = get(cartAtom);

    const { selectedCart: updatedRetailerCart, moved } = moveBetweenDeliveries(
      cart,
      retailerId,
      sourceDeliveryId,
      targetDeliveryId,
      catalogueIds,
      siloId,
    );

    // Update the cart.
    if (moved) {
      cart[retailerId] = updatedRetailerCart;
      set(cartAtom, { ...cart });
    }
  },
);

type SetCartItemQuantityOptions = {
  retailerId: string;
  deliveryId: string;
  catalogueId: string;
  quantity: number;
};

export const setCartItemQuantityAtom = atom(
  (get) => get(cartAtom),
  (
    get,
    set,
    {
      retailerId,
      deliveryId,
      catalogueId,
      quantity,
    }: SetCartItemQuantityOptions,
  ) => {
    const cart = get(cartAtom);

    const { cart: updatedCart, quantityUpdated } = setCartItemQuantity(
      cart,
      retailerId,
      quantity,
      deliveryId,
      catalogueId,
    );

    if (quantityUpdated) set(cartAtom, { ...updatedCart });
  },
);

type ChangeDeliveryAddressOptions = {
  retailerId: string;
  deliveryId: string;
  newAddress: OrderDeliveryAddress;
};

export const changeDeliveryAddressAtom = atom(
  (get) => get(cartAtom),
  (
    get,
    set,
    { retailerId, deliveryId, newAddress }: ChangeDeliveryAddressOptions,
  ) => {
    const cart = get(cartAtom);

    const { cart: updatedCart, addressUpdated } = changeDeliveryAddress(
      cart,
      retailerId,
      deliveryId,
      newAddress,
    );

    if (addressUpdated) set(cartAtom, { ...updatedCart });
  },
);

type changeLocationIDAtomOptions = {
  retailerId: string;
  deliveryId: string;
  locationID: string;
};

export const changeLocationIDAtom = atom(
  (get) => get(cartAtom),
  (
    get,
    set,
    { retailerId, deliveryId, locationID }: changeLocationIDAtomOptions,
  ) => {
    const cart = get(cartAtom);

    const { cart: updatedCart, idUpdated } = changeLocationID(
      cart,
      retailerId,
      deliveryId,
      locationID,
    );

    if (idUpdated) set(cartAtom, { ...updatedCart });
  },
);

interface changeSiloIdAtomOptions {
  retailerId: string;
  deliveryId: string;
  siloId: string;
}

export const changeSiloIdAtom = atom(
  (get) => get(cartAtom),
  (get, set, { retailerId, deliveryId, siloId }: changeSiloIdAtomOptions) => {
    const cart = get(cartAtom);

    const { cart: updatedCart, idUpdated } = changeSiloId(
      cart,
      retailerId,
      deliveryId,
      siloId,
    );

    if (idUpdated) set(cartAtom, { ...updatedCart });
  },
);

interface changeDeliveryDatesAtomOptions {
  retailerId: string;
  deliveryId: string;
  from?: string;
  to?: string;
}

export const changeDeliveryDatesAtom = atom(
  (get) => get(cartAtom),
  (
    get,
    set,
    { retailerId, deliveryId, from, to }: changeDeliveryDatesAtomOptions,
  ) => {
    const cart = get(cartAtom);

    const { cart: updatedCart, dateUpdated } = changeDeliveryDates(
      cart,
      retailerId,
      deliveryId,
      from,
      to,
    );

    if (dateUpdated) set(cartAtom, { ...updatedCart });
  },
);

type ClearCartOptions = {
  retailerId: string;
};

export const clearCartAtom = atom(
  (get) => get(cartAtom),
  (get, set, { retailerId }: ClearCartOptions) => {
    const cart = get(cartAtom);

    if (!cart[retailerId]) return; // If the retailer's cart doesn't exist, do nothing

    // Set the retailer's cart to an empty array
    cart[retailerId] = [];

    // Update the state with the new cart
    set(cartAtom, { ...cart });
  },
);

export const updateComment = (
  cart: Cart,
  key: string,
  deliveryId: string,
  comment: string,
) => {
  const selectedCart = cart[key];
  if (!selectedCart) return { cart, addressUpdated: false };

  const deliveryToUpdate = selectedCart.find(
    (delivery) => delivery.id === deliveryId,
  );
  if (!deliveryToUpdate) return { cart, idUpdated: false };

  // Update the delivery address
  deliveryToUpdate.comments = comment;

  return { cart, idUpdated: true };
};

interface deliveryCommentsOptions {
  retailerId: string;
  deliveryId: string;
  comment: string;
}

export const updateDeliyveryCommentAtom = atom(
  (get) => get(cartAtom),
  (get, set, { retailerId, deliveryId, comment }: deliveryCommentsOptions) => {
    const cart = get(cartAtom);

    const { cart: updatedCart, idUpdated } = updateComment(
      cart,
      retailerId,
      deliveryId,
      comment,
    );

    if (idUpdated) set(cartAtom, { ...updatedCart });
  },
);
